import React, { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Target, Coins, Check, Loader2, Trophy } from "lucide-react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * ProfileMissionsCard — real active missions + this user's daily progress + claim CTA.
 */
export default function ProfileMissionsCard() {
  const [missions, setMissions] = useState(null);
  const [claiming, setClaiming] = useState("");

  const load = async () => {
    try {
      const r = await axios.get(`${API}/missions/today`);
      setMissions(Array.isArray(r.data) ? r.data : []);
    } catch (e) {
      setMissions([]);
    }
  };

  useEffect(() => { load(); }, []);

  const claim = async (mid) => {
    setClaiming(mid);
    try {
      const r = await axios.post(`${API}/missions/${mid}/claim`);
      toast.success(`+${r.data.reward} moedas recebidas!`);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Não foi possível resgatar");
    }
    setClaiming("");
  };

  if (missions === null) {
    return (
      <Card className="p-6 flex items-center gap-2" data-testid="profile-missions-loading">
        <Loader2 className="w-4 h-4 animate-spin" /> Carregando missões…
      </Card>
    );
  }

  if (!missions.length) return null;

  const completed = missions.filter((m) => m.completed_at).length;
  const total_reward = missions.reduce((a, m) => a + (m.coin_reward || 0), 0);

  return (
    <Card className="p-6 relative overflow-hidden" data-testid="profile-missions-card">
      <div aria-hidden className="pointer-events-none absolute inset-0" style={{ background: "radial-gradient(600px circle at 100% 0%, rgba(59,130,246,.15), transparent 40%)" }} />
      <div className="relative flex items-start justify-between gap-3 mb-4">
        <div className="flex items-start gap-3">
          <div className="w-11 h-11 rounded-2xl bg-blue-500/15 text-blue-600 flex items-center justify-center shrink-0">
            <Target className="w-5 h-5" />
          </div>
          <div>
            <div className="text-[10px] uppercase tracking-widest text-muted-foreground font-bold">Missões de hoje</div>
            <h3 className="text-lg font-bold tracking-tight">Complete e ganhe moedas</h3>
            <div className="text-xs text-muted-foreground mt-0.5">
              {completed} de {missions.length} concluídas · pool total <span className="text-yellow-600 font-bold">+{total_reward}</span>
            </div>
          </div>
        </div>
        {completed === missions.length && (
          <span className="inline-flex items-center gap-1 text-emerald-700 text-[11px] font-bold">
            <Trophy className="w-3.5 h-3.5" /> Dia completo!
          </span>
        )}
      </div>

      <ul className="relative space-y-2" data-testid="profile-missions-list">
        {missions.map((m) => {
          const goal = m.goal_count || 1;
          const prog = Math.min(goal, m.progress || 0);
          const pct = Math.round((prog / goal) * 100);
          const done  = !!m.completed_at;
          const claimed = !!m.claimed_at;
          return (
            <li
              key={m.mission_id}
              className="p-3 rounded-xl border border-border bg-card/70 flex items-center gap-3"
              data-testid={`profile-mission-${m.mission_id}`}
            >
              <div className={`w-9 h-9 rounded-xl flex items-center justify-center shrink-0 ${claimed ? "bg-emerald-500/15 text-emerald-600" : done ? "bg-yellow-500/15 text-yellow-600" : "bg-muted text-muted-foreground"}`}>
                {claimed ? <Check className="w-4 h-4" /> : <Coins className="w-4 h-4" />}
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2">
                  <div className="text-sm font-semibold truncate">{m.title}</div>
                  <span className="text-[10px] font-black text-yellow-600 shrink-0">+{m.coin_reward}</span>
                </div>
                <div className="mt-1 flex items-center gap-2">
                  <div className="flex-1 h-1.5 rounded-full bg-muted overflow-hidden">
                    <div
                      className={`h-full ${claimed ? "bg-emerald-500" : done ? "bg-yellow-500" : "bg-blue-500"}`}
                      style={{ width: `${pct}%` }}
                    />
                  </div>
                  <span className="text-[10px] font-mono text-muted-foreground shrink-0">
                    {prog}/{goal}
                  </span>
                </div>
              </div>
              {done && !claimed && (
                <Button
                  size="sm"
                  className="rounded-full gap-1 h-8"
                  onClick={() => claim(m.mission_id)}
                  disabled={claiming === m.mission_id}
                  data-testid={`profile-mission-claim-${m.mission_id}`}
                >
                  {claiming === m.mission_id ? <Loader2 className="w-3 h-3 animate-spin" /> : <Coins className="w-3 h-3" />}
                  Resgatar
                </Button>
              )}
              {claimed && (
                <span className="text-[10px] font-bold text-emerald-700 shrink-0" data-testid={`profile-mission-claimed-${m.mission_id}`}>Resgatada</span>
              )}
            </li>
          );
        })}
      </ul>
    </Card>
  );
}
